<template>
  <div
    class="sidd-message-bubble"
    :class="{
      'sidd-message-bubble--user': isUserMessage,
      'sidd-message-bubble--bot': !isUserMessage,
      'sidd-message-bubble--error': isErrorMessage,
      'sidd-message-bubble--loading': isLoading,
    }"
  >
    <div class="sidd-message-bubble__content">
      <!-- Message text -->
      <div class="sidd-message-bubble__text">
        <p class="sidd-message-bubble__message">
          {{ displayedText
          }}<span v-if="isStreaming" class="sidd-message-bubble__cursor"></span>
        </p>
      </div>

      <!-- Timestamp -->
      <!-- Hide timestamp in v1 -->
      <!-- <div class="sidd-message-bubble__timestamp">
        {{ formatTimestamp(message.timestamp) }}
      </div> -->

      <!-- Bot message actions -->
      <div
        v-if="!isUserMessage && !isLoading"
        class="sidd-message-bubble__actions"
      >
        <Button
          variant="ghost"
          size="sm"
          class="sidd-message-bubble__action"
          @click="handleCopy"
          :aria-label="copyButtonLabel"
          title="Copy message"
        >
          <Copy class="h-4 w-4" />
        </Button>

        <Button
          variant="ghost"
          size="sm"
          class="sidd-message-bubble__action"
          @click="handleRefresh"
          :aria-label="refreshButtonLabel"
          title="Regenerate response"
        >
          <RotateCcw class="h-4 w-4" />
        </Button>

        <Button
          variant="ghost"
          size="sm"
          class="sidd-message-bubble__action"
          :class="{
            'sidd-message-bubble__action--active': feedback === 'positive',
          }"
          @click="handleThumbsUp"
          :aria-label="thumbsUpLabel"
          title="Good response"
        >
          <ThumbsUp class="h-4 w-4" />
        </Button>

        <Button
          variant="ghost"
          size="sm"
          class="sidd-message-bubble__action"
          :class="{
            'sidd-message-bubble__action--active': feedback === 'negative',
          }"
          @click="handleThumbsDown"
          :aria-label="thumbsDownLabel"
          title="Poor response"
        >
          <ThumbsDown class="h-4 w-4" />
        </Button>

        <!-- Sources button -->
        <Button
          v-if="message.sourceDocuments?.length && showSources"
          variant="ghost"
          size="sm"
          class="sidd-message-bubble__action sidd-message-bubble__sources-toggle"
          :class="{
            'sidd-message-bubble__action--active': showSourcesExpanded,
          }"
          @click="toggleSources"
          :aria-label="sourcesButtonLabel"
          title="View sources"
        >
          <FileText class="h-4 w-4" />
        </Button>
      </div>

      <!-- Sources section -->
      <div
        v-if="
          message.sourceDocuments?.length && showSources && showSourcesExpanded
        "
        class="sidd-message-bubble__sources"
      >
        <h4 class="sidd-message-bubble__sources-title">Sources</h4>
        <ul class="sidd-message-bubble__sources-list">
          <li
            v-for="(source, index) in parsedSourceDocuments"
            :key="index"
            class="sidd-message-bubble__source"
            :class="{ 'sidd-message-bubble__source--clickable': source.url }"
            @click="source.url && openSourceUrl(source.url, index)"
            :title="source.originalName"
          >
            <div class="sidd-message-bubble__source-icon">
              <Globe v-if="source.url" class="h-3.5 w-3.5" />
              <FileText v-else class="h-3.5 w-3.5" />
            </div>
            <div class="sidd-message-bubble__source-content">
              <span class="sidd-message-bubble__source-text">{{
                source.parsedName
              }}</span>
              <Badge
                v-if="source.extension"
                variant="secondary"
                class="sidd-message-bubble__source-badge"
              >
                {{ source.extension }}
              </Badge>
            </div>
            <ExternalLink
              v-if="source.url"
              class="h-3.5 w-3.5 sidd-message-bubble__source-link"
            />
          </li>
        </ul>
      </div>

      <div
        v-if="message.followUpQuestions?.length && !isLoading"
        class="sidd-message-bubble__follow-up"
      >
        <div class="sidd-message-bubble__follow-up-list">
          <GradientButton
            v-for="(question, index) in message.followUpQuestions"
            :key="index"
            size="sm"
            full-width
            multiline
            @click="handleFollowUpQuestionClick(question, index)"
          >
            {{ question }}
          </GradientButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import {
  Copy,
  RotateCcw,
  ThumbsUp,
  ThumbsDown,
  FileText,
  Globe,
  ExternalLink,
} from 'lucide-vue-next'
import { Button } from '@/lib/ui/button'
import { Badge } from '@/lib/ui/badge'
import { GradientButton } from '@/lib/ui/gradient-button'
import { logger } from '@/utils/logger'
import { isPunctuation, parseSourceDocuments } from '@/utils/text'
import { useChatSession } from '@/composables/useChatSession'
import { useAnalytics } from '@/composables/useAnalytics'
import { ANALYTICS_EVENTS, ANALYTICS_SOURCE } from '@/types/analytics-types'
import { MESSAGE_CONSTANTS } from '@/types/constants'
import type { IMessageBubbleProps, IMessageBubbleEmits } from './types'

const props = withDefaults(defineProps<IMessageBubbleProps>(), {
  showSources: true,
  isLoading: false,
})

const emit = defineEmits<IMessageBubbleEmits>()

const chatSession = useChatSession({
  persistMessages: true,
  autoScrollToBottom: true,
})
const analytics = useAnalytics()

// Local state
const showSourcesExpanded = ref(false)
const feedback = ref<'positive' | 'negative' | null>(null)
const displayedText = ref('')
const isStreaming = ref(false)
const animationFrameId = ref<number | null>(null)
const targetText = ref('')
const currentIndex = ref(0)

// Computed
const isUserMessage = computed(() =>
  Boolean(props.message.userMessage && !props.message.answer)
)
const isErrorMessage = computed(
  () => !props.message.success || Boolean(props.message.error)
)
const messageContent = computed(
  () => props.message.userMessage || props.message.answer
)
const isBotMessage = computed(() => !isUserMessage.value)

const parsedSourceDocuments = computed(() =>
  parseSourceDocuments(props.message.sourceDocuments || [])
)

const CHARS_PER_FRAME = 2
const BASE_DELAY = 25
const PUNCTUATION_DELAY = 200

const typewriterEffect = () => {
  if (currentIndex.value >= targetText.value.length) {
    displayedText.value = targetText.value
    isStreaming.value = false
    if (animationFrameId.value !== null) {
      cancelAnimationFrame(animationFrameId.value)
      animationFrameId.value = null
    }
    return
  }

  // Add characters
  const charsToAdd = Math.min(
    CHARS_PER_FRAME,
    targetText.value.length - currentIndex.value
  )
  currentIndex.value += charsToAdd
  displayedText.value = targetText.value.substring(0, currentIndex.value)

  const lastChar = displayedText.value[displayedText.value.length - 1] || ''
  const hasPunctuation = isPunctuation(lastChar)
  const delay = hasPunctuation ? BASE_DELAY + PUNCTUATION_DELAY : BASE_DELAY

  setTimeout(() => {
    if (currentIndex.value < targetText.value.length) {
      animationFrameId.value = requestAnimationFrame(typewriterEffect)
    }
  }, delay)
}

watch(
  () => props.message.answer,
  newAnswer => {
    if (!newAnswer || isUserMessage.value) {
      displayedText.value = messageContent.value
      return
    }

    targetText.value = newAnswer

    if (displayedText.value === '' && props.message.userMessage) {
      displayedText.value = props.message.userMessage
      return
    }

    const isStillStreaming =
      props.message.status !== MESSAGE_CONSTANTS.STATUS.SENT &&
      props.message.status !== MESSAGE_CONSTANTS.STATUS.FAILED

    if (isStillStreaming) {
      isStreaming.value = true

      if (animationFrameId.value !== null) {
        cancelAnimationFrame(animationFrameId.value)
      }

      currentIndex.value = displayedText.value.length
      animationFrameId.value = requestAnimationFrame(typewriterEffect)
    } else {
      displayedText.value = targetText.value
      isStreaming.value = false
    }
  },
  { immediate: true }
)

watch(
  () => props.message.status,
  newStatus => {
    if (
      (newStatus === MESSAGE_CONSTANTS.STATUS.SENT ||
        newStatus === MESSAGE_CONSTANTS.STATUS.FAILED) &&
      isBotMessage.value
    ) {
      if (animationFrameId.value !== null) {
        cancelAnimationFrame(animationFrameId.value)
        animationFrameId.value = null
      }
      displayedText.value = targetText.value
      isStreaming.value = false
    }
  }
)

if (messageContent.value) {
  displayedText.value = messageContent.value
}

onUnmounted(() => {
  if (animationFrameId.value !== null) {
    cancelAnimationFrame(animationFrameId.value)
  }
})

const copyButtonLabel = computed(
  () => `Copy message: ${messageContent.value.substring(0, 50)}...`
)
const refreshButtonLabel = computed(
  () => `Regenerate response for: ${messageContent.value.substring(0, 50)}...`
)
const thumbsUpLabel = computed(
  () => `Mark as helpful: ${messageContent.value.substring(0, 50)}...`
)
const thumbsDownLabel = computed(
  () => `Mark as unhelpful: ${messageContent.value.substring(0, 50)}...`
)
const sourcesButtonLabel = computed(() =>
  showSourcesExpanded.value
    ? 'Hide sources'
    : `View ${props.message.sourceDocuments?.length || 0} sources`
)

// Methods
const handleCopy = async () => {
  try {
    await navigator.clipboard.writeText(messageContent.value)

    analytics.trackEvent({
      eventName: ANALYTICS_EVENTS.ANSWER_COPY_CLICKED,
      timestamp: new Date().toISOString(),
      payload: {
        messageId: props.message.replyToId,
        characterCount: messageContent.value.length,
      },
      source: ANALYTICS_SOURCE,
      sessionId: props.message.sessionId,
    })

    emit('copy', props.message)
  } catch (error) {
    logger.error(
      'Failed to copy message',
      error,
      { component: 'MessageBubble' },
      error as Error
    )
  }
}

const handleRefresh = async () => {
  try {
    const activeElement = document.activeElement as HTMLElement
    if (activeElement && typeof activeElement.blur === 'function') {
      activeElement.blur()
    }

    analytics.trackEvent({
      eventName: ANALYTICS_EVENTS.REGENERATE_ANSWER_CLICKED,
      timestamp: new Date().toISOString(),
      payload: {
        messageId: props.message.replyToId,
      },
      source: ANALYTICS_SOURCE,
      sessionId: props.message.sessionId,
    })

    await chatSession.retryMessage(props.message.id)
  } catch (error) {
    logger.error(
      'Failed to retry message',
      error,
      { component: 'MessageBubble' },
      error as Error
    )
    emit('refresh', props.message)
  }
}

const handleThumbsUp = () => {
  feedback.value = feedback.value === 'positive' ? null : 'positive'

  analytics.trackEvent({
    eventName: ANALYTICS_EVENTS.THUMBS_UP_CLICKED,
    timestamp: new Date().toISOString(),
    payload: {
      messageId: props.message.replyToId,
    },
    source: ANALYTICS_SOURCE,
    sessionId: props.message.sessionId,
  })

  emit('feedback', props.message, 'positive')
}

const handleThumbsDown = () => {
  feedback.value = feedback.value === 'negative' ? null : 'negative'

  analytics.trackEvent({
    eventName: ANALYTICS_EVENTS.THUMBS_DOWN_CLICKED,
    timestamp: new Date().toISOString(),
    payload: {
      messageId: props.message.replyToId,
    },
    source: ANALYTICS_SOURCE,
    sessionId: props.message.sessionId,
  })

  emit('feedback', props.message, 'negative')
}

const sourcesExpandedAt = ref<number | null>(null)
const sourcesDocumentsClickedWhileExpanded = ref(0)

const toggleSources = () => {
  const wasExpanded = showSourcesExpanded.value
  showSourcesExpanded.value = !showSourcesExpanded.value

  if (showSourcesExpanded.value) {
    sourcesExpandedAt.value = Date.now()
    sourcesDocumentsClickedWhileExpanded.value = 0

    analytics.trackEvent({
      eventName: ANALYTICS_EVENTS.INLINE_SOURCES_EXPANDED,
      timestamp: new Date().toISOString(),
      payload: {
        messageId: props.message.id,
        sourceCount: props.message.sourceDocuments?.length || 0,
        sourceNames: props.message.sourceDocuments?.map(s => s.name) || [],
      },
      source: ANALYTICS_SOURCE,
      sessionId: props.message.sessionId,
    })
  } else if (wasExpanded) {
    const timeExpanded = sourcesExpandedAt.value
      ? Date.now() - sourcesExpandedAt.value
      : 0

    analytics.trackEvent({
      eventName: ANALYTICS_EVENTS.INLINE_SOURCES_COLLAPSED,
      timestamp: new Date().toISOString(),
      payload: {
        messageId: props.message.id,
        timeExpanded: timeExpanded,
        documentsClicked: sourcesDocumentsClickedWhileExpanded.value,
        sourceCount: props.message.sourceDocuments?.length || 0,
      },
      source: ANALYTICS_SOURCE,
      sessionId: props.message.sessionId,
    })

    sourcesExpandedAt.value = null
    sourcesDocumentsClickedWhileExpanded.value = 0
  }
}

const openSourceUrl = (url: string, index: number) => {
  if (showSourcesExpanded.value) {
    sourcesDocumentsClickedWhileExpanded.value++
  }

  analytics.trackEvent({
    eventName: ANALYTICS_EVENTS.SOURCE_DOCUMENT_CLICKED,
    timestamp: new Date().toISOString(),
    payload: {
      documentName: props.message.sourceDocuments?.[index]?.name || '',
      documentUrl: url,
      documentIndex: index,
      totalDocuments: props.message.sourceDocuments?.length || 0,
      location: 'inline',
      messageId: props.message.id,
      sessionId: props.message.sessionId,
    },
    source: ANALYTICS_SOURCE,
    sessionId: props.message.sessionId,
  })

  window.open(url, '_blank', 'noopener,noreferrer')
}

const handleFollowUpQuestionClick = (question: string, index: number) => {
  analytics.trackEvent({
    eventName: ANALYTICS_EVENTS.FOLLOW_UP_QUESTION_CLICKED,
    timestamp: new Date().toISOString(),
    payload: {
      question: question,
      questionIndex: index,
      totalQuestions: props.message.followUpQuestions?.length || 0,
      parentMessageId: props.message.id,
      characterCount: question.length,
    },
    source: ANALYTICS_SOURCE,
    sessionId: props.message.sessionId,
  })

  emit('followUpQuestionClick', question, props.message.id)
}
</script>

<style>
.sidd-message-bubble {
  display: flex;
  max-width: 85%;
  margin-bottom: 16px;
  animation: slideIn 0.3s ease-out;
}

.sidd-message-bubble--user {
  align-self: flex-end;
  justify-content: flex-end;
  margin-left: auto;
}

.sidd-message-bubble--bot {
  align-self: flex-start;
  justify-content: flex-start;
  margin-right: auto;
}

.sidd-message-bubble__content {
  border-radius: 18px;
  padding: 8px 20px;
  position: relative;
  word-wrap: break-word;
  overflow-wrap: break-word;
  min-width: 120px;
}

.sidd-message-bubble--user .sidd-message-bubble__content {
  background: hsl(var(--muted));
  color: hsl(var(--foreground));
  border-bottom-right-radius: 6px;
}

.sidd-message-bubble--bot .sidd-message-bubble__content {
  background: linear-gradient(
    135deg,
    hsl(var(--primary) / 0.03) 0%,
    hsl(var(--primary) / 0.05) 25%,
    rgba(255, 255, 255, 0.95) 50%,
    hsl(var(--primary) / 0.04) 75%,
    hsl(var(--primary) / 0.02) 100%
  );
  color: hsl(var(--foreground));
  border: 1px solid hsl(var(--primary) / 0.08);
  border-bottom-left-radius: 6px;
}

.sidd-message-bubble--error .sidd-message-bubble__content {
  background: hsl(var(--destructive) / 0.1);
  border: 1px solid hsl(var(--destructive) / 0.2);
  color: hsl(var(--destructive));
}

.sidd-message-bubble__text {
  margin-bottom: 6px;
}

.sidd-message-bubble__message {
  margin: 0;
  font-size: 0.9375em;
  line-height: 1.6;
  white-space: pre-wrap;
  position: relative;
}

.sidd-message-bubble__cursor {
  display: inline-block;
  width: 2px;
  height: 1em;
  background: hsl(var(--primary));
  margin-left: 2px;
  vertical-align: text-bottom;
  animation: cursorBlink 1s infinite;
}

@keyframes cursorBlink {
  0%,
  49% {
    opacity: 1;
  }
  50%,
  100% {
    opacity: 0;
  }
}

.sidd-message-bubble__timestamp {
  font-size: 0.6875em;
  color: hsl(var(--muted-foreground));
  margin-top: 6px;
  text-align: right;
}

.sidd-message-bubble--user .sidd-message-bubble__timestamp {
  color: hsl(var(--muted-foreground));
}

.sidd-message-bubble__actions {
  display: flex;
  gap: 2px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid hsl(var(--border) / 0.2);
}

.sidd-message-bubble__action {
  min-width: 32px;
  height: 32px;
  padding: 0;
}

.sidd-message-bubble__action--active {
  background: hsl(var(--primary) / 0.1);
  color: hsl(var(--primary));
}

.sidd-message-bubble__sources {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid hsl(var(--border) / 0.2);
}

.sidd-message-bubble__sources-title {
  font-size: 0.75em;
  font-weight: 600;
  color: hsl(var(--foreground));
  margin: 0 0 8px 0;
}

.sidd-message-bubble__sources-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sidd-message-bubble__source {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  background: hsl(var(--background) / 0.5);
  border: 1px solid hsl(var(--border) / 0.2);
}

.sidd-message-bubble__source--clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.sidd-message-bubble__source--clickable:hover {
  background: hsl(var(--primary) / 0.1);
  border-color: hsl(var(--primary) / 0.3);
}

.sidd-message-bubble__source-icon {
  flex-shrink: 0;
  color: hsl(var(--muted-foreground));
  margin-top: 1px;
}

.sidd-message-bubble__source-content {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
}

.sidd-message-bubble__source-text {
  font-size: 0.75em;
  line-height: 1.4;
  color: hsl(var(--foreground));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
  min-width: 0;
}

.sidd-message-bubble__source-badge {
  flex-shrink: 0;
}

.sidd-message-bubble__source-link {
  flex-shrink: 0;
  color: hsl(var(--primary));
  margin-top: 1px;
}

.sidd-message-bubble__follow-up {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid hsl(var(--border) / 0.2);
}

.sidd-message-bubble__follow-up-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .sidd-message-bubble {
    animation: none;
  }
}
</style>
